/**
 * トースト通知のスタイル定義 (light / dark 両対応)
 *
 * 背景・アイコン・テキスト・閉じるボタンの 4 パートを variant ごとに保持する。
 *
 * 過去経緯 (2026-05-26 修正):
 *   旧 ToastContainer.tsx は dark モード時の text-color が 4 variant とも
 *   `text-gray-200` に統一されており、`bg-green-900/80` の上にグレー文字
 *   (緑地グレー文字) / `bg-red-900/80` の上にグレー文字 (赤地グレー文字、
 *   「赤地白文字反映漏れ」) になる視認性バグがあった。
 *
 *   `Toast.tsx` 側は c2bcc2a で variant 別の dark text-color に修正済みだったが、
 *   実際の利用箇所 (`AppLayout.tsx`) が `ToastContainer.tsx` のみで `Toast.tsx`
 *   は dead code だったため、本修正が live UI に反映されていなかった。
 *
 *   本ファイルで TYPE_STYLES を一元管理し、両コンポーネントが同じソースから
 *   読み取ることで再発を防ぐ (Toast.tsx は dead code として削除)。
 *
 * デザイン方針:
 *   - light: success / warning / info は pastel 背景 + 濃色テキスト
 *     (例: `bg-green-100` + `text-green-900`)
 *   - light error のみ警告レベル最高につき反転 (`bg-red-600` + 白文字、
 *     `ConfirmDialog` の `variant='danger'` と統一)
 *   - dark: 濃色背景 + 薄色テキスト (variant に揃えた色相を使う、
 *     例: `dark:bg-green-900` + `dark:text-green-100`)
 *   - **dark モードで全 variant を `dark:text-gray-200` に統一しない**
 *     (色相を保つことでステータスを直感的に識別できるようにする)
 */
import type { ToastType } from '@/types';

export interface ToastStyle {
  /** トースト全体の背景・枠線 (rounded-lg は呼び出し側で付与) */
  container: string;
  /** アイコン色 */
  icon: string;
  /** メッセージテキスト色 */
  text: string;
  /** 閉じるボタンの基本色 + hover */
  close: string;
}

export const TOAST_STYLES: Record<ToastType, ToastStyle> = {
  success: {
    container: 'bg-green-100 border-green-400 dark:bg-green-900 dark:border-green-700',
    icon: 'text-green-700 dark:text-green-200',
    text: 'text-green-900 dark:text-green-100',
    close: 'text-green-700 hover:text-green-900 dark:text-green-200 dark:hover:text-white',
  },
  error: {
    // light は反転 (bg-red-600 + 白文字)、dark は濃赤背景 + 薄赤文字
    container: 'bg-red-600 border-red-700 dark:bg-red-900 dark:border-red-700',
    icon: 'text-white dark:text-red-200',
    text: 'text-white dark:text-red-100',
    close: 'text-white/80 hover:text-white dark:text-red-200 dark:hover:text-white',
  },
  warning: {
    container: 'bg-yellow-100 border-yellow-400 dark:bg-yellow-900 dark:border-yellow-700',
    icon: 'text-yellow-800 dark:text-yellow-200',
    text: 'text-yellow-900 dark:text-yellow-100',
    close: 'text-yellow-800 hover:text-yellow-900 dark:text-yellow-200 dark:hover:text-white',
  },
  info: {
    container: 'bg-blue-100 border-blue-400 dark:bg-blue-900 dark:border-blue-700',
    icon: 'text-blue-700 dark:text-blue-200',
    text: 'text-blue-900 dark:text-blue-100',
    close: 'text-blue-700 hover:text-blue-900 dark:text-blue-200 dark:hover:text-white',
  },
};
